import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import QuotationService, { Quotation, QuotationItem } from '../../services/quotation.service';
import { insuranceClientService } from '../../services/insuranceClientService';
import ProductService from '../../services/product.service';
import { NumberTextField } from '../../components/common';

interface LineItem {
  tempId: string;
  id?: number;
  product: any;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxPercentage: number; // 0–100; tax amount is computed from (subtotal - discount) * taxPercentage/100
  total: number;
}

const QuotationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [insuranceClients, setInsuranceClients] = useState<any[]>([]);
  const [insuranceClientsLoading, setInsuranceClientsLoading] = useState(false);
  const [selectedInsuranceClient, setSelectedInsuranceClient] = useState<any>(null);
  
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<Quotation>({
    quotationNumber: `QT-${Date.now()}`,
    customer: 0,
    insuranceClient: 0,
    date: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    status: 'draft',
    notes: '',
  });

  useEffect(() => {
    fetchInsuranceClients();
    fetchProducts();
    if (isEdit && id) {
      fetchQuotation(parseInt(id));
    }
  }, [id, isEdit]);

  // Refetch insurance clients when window regains focus (e.g. after creating one in new tab)
  useEffect(() => {
    const onFocus = () => fetchInsuranceClients();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchInsuranceClients = async () => {
    try {
      setInsuranceClientsLoading(true);
      const response = await insuranceClientService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setInsuranceClients(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance clients:', error);
    } finally {
      setInsuranceClientsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
        populate: 'category',
      });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchQuotation = async (quotationId: number) => {
    try {
      setLoading(true);
      const response = await QuotationService.getOne(quotationId);
      const quoteData = response.data?.data;

      if (quoteData) {
        const attrs = quoteData.attributes;
        setFormData({
          quotationNumber: attrs.quotationNumber,
          customer: attrs.customer?.data?.id || 0,
          insuranceClient: attrs.insuranceClient?.data?.id || 0,
          date: attrs.date,
          validUntil: attrs.validUntil || '',
          subtotal: attrs.subtotal || 0,
          tax: attrs.tax || 0,
          discount: attrs.discount || 0,
          total: attrs.total || 0,
          status: attrs.status || 'draft',
          notes: attrs.notes || '',
          termsAndConditions: attrs.termsAndConditions || '',
        });

        if (attrs.insuranceClient?.data) {
          setSelectedInsuranceClient(attrs.insuranceClient.data);
        } else if (attrs.customer?.data) {
          setSelectedInsuranceClient(attrs.customer.data);
        }

        // Fetch line items
        const itemsResponse = await QuotationService.getItems(quotationId);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => {
          const qty = item.attributes?.quantity || 1;
          const price = item.attributes?.unitPrice || 0;
          const discount = Number(item.attributes?.discount) || 0;
          const taxAmount = Number(item.attributes?.tax) || 0;
          const afterDiscount = Math.max(0, qty * price - discount);
          const taxPercentage = afterDiscount > 0 ? (taxAmount / afterDiscount) * 100 : 0;
          return {
            tempId: `item-${item.id}`,
            id: item.id,
            product: item.attributes?.product?.data,
            description: item.attributes?.description || '',
            quantity: qty,
            unitPrice: price,
            discount,
            taxPercentage,
            total: item.attributes?.total || 0,
          };
        });
        
        setLineItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching quotation:', error);
      setError('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleInsuranceClientChange = (event: any, newValue: any) => {
    setSelectedInsuranceClient(newValue);
    setFormData({ ...formData, insuranceClient: newValue?.id || 0, customer: 0 });
  };

  const addLineItem = () => {
    const newItem: LineItem = {
      tempId: `temp-${Date.now()}`,
      product: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      taxPercentage: 0,
      total: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          
          // Auto-fill price and description when product is selected
          if (field === 'product' && value && typeof value === 'object') {
            updated.unitPrice = Number(value.attributes?.price) || 0;
            updated.description = value.attributes?.name || '';
          }
          
          // Convert numeric fields to numbers
          if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxPercentage') {
            updated[field] = Number(value) || 0;
          }
          
          // Calculate line total: (subtotal - discount) * (1 + taxPercentage/100)
          const qty = Number(updated.quantity) || 0;
          const price = Number(updated.unitPrice) || 0;
          const discount = Number(updated.discount) || 0;
          const taxPct = Number(updated.taxPercentage) || 0;
          
          const subtotal = qty * price;
          const afterDiscount = Math.max(0, subtotal - discount);
          updated.total = afterDiscount * (1 + taxPct / 100);
          
          return updated;
        }
        return item;
      })
    );
  };

  const updateLineItemFields = (tempId: string, updates: Partial<LineItem>) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.tempId !== tempId) return item;
        const updated = { ...item, ...updates };
        const qty = Number(updated.quantity) || 0;
        const price = Number(updated.unitPrice) || 0;
        const discount = Number(updated.discount) || 0;
        const taxPct = Number(updated.taxPercentage) || 0;
        const subtotal = qty * price;
        const afterDiscount = Math.max(0, subtotal - discount);
        updated.total = afterDiscount * (1 + taxPct / 100);
        return updated;
      })
    );
  };

  // Calculate quotation totals
  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = Number(item.quantity) * Number(item.unitPrice);
      return sum + itemSubtotal;
    }, 0);

    const totalDiscount = lineItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const totalTax = lineItems.reduce((sum, item) => {
      const itemSubtotal = Number(item.quantity) * Number(item.unitPrice);
      const afterDiscount = Math.max(0, itemSubtotal - (Number(item.discount) || 0));
      return sum + afterDiscount * ((Number(item.taxPercentage) || 0) / 100);
    }, 0);
    const total = subtotal - totalDiscount + totalTax;

    setFormData((prev) => ({
      ...prev,
      subtotal: Number(subtotal) || 0,
      discount: Number(totalDiscount) || 0,
      tax: Number(totalTax) || 0,
      total: Number(total) || 0,
    }));
  }, [lineItems]);

  const validateForm = (): boolean => {
    if (!formData.insuranceClient && !formData.customer) {
      setError('Please select an insurance client');
      return false;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    if (lineItems.some(item => !item.product && !(item.description || '').trim())) {
      setError('Each line item must have a product selected or a description');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let quotationId: number;

      // Create or update quotation
      if (isEdit && id) {
        await QuotationService.update(parseInt(id), formData);
        quotationId = parseInt(id);
        setSuccess('Quotation updated!');
      } else {
        const response = await QuotationService.create(formData);
        quotationId = response.data?.data?.id;
        setSuccess('Quotation created!');
      }

      // Save line items
      if (quotationId) {
        // Delete old items if editing
        if (isEdit) {
          const oldItemsResponse = await QuotationService.getItems(quotationId);
          const oldItems = oldItemsResponse.data?.data || [];
          await Promise.all(
            oldItems.map((item: any) => QuotationService.deleteItem(item.id))
          );
        }

        // Create new items
        await Promise.all(
          lineItems.map((item) => {
            const itemSubtotal = Number(item.quantity) * Number(item.unitPrice);
            const afterDiscount = Math.max(0, itemSubtotal - (Number(item.discount) || 0));
            const taxAmount = afterDiscount * ((Number(item.taxPercentage) || 0) / 100);
            return QuotationService.createItem({
              quotation: quotationId,
              product: item.product?.id ?? null,
              description: (item.description || '').trim() || (item.product?.attributes?.name || ''),
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount,
              tax: taxAmount,
              total: item.total,
            });
          })
        );
      }

      setTimeout(() => {
        navigate('/quotations');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving quotation:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save quotation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Quotation' : 'New Quotation'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Header Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quotation Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Quotation Number"
                  name="quotationNumber"
                  value={formData.quotationNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </TextField>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                <Autocomplete
                  sx={{ minWidth: 280, flex: 1 }}
                  value={selectedInsuranceClient}
                  onChange={handleInsuranceClientChange}
                  options={insuranceClients}
                  loading={insuranceClientsLoading}
                  getOptionLabel={(option) =>
                    option.attributes?.customerName || option.attributes?.vinNumber || ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Insurance Client *"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {insuranceClientsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => window.open('/insurance-clients', '_blank')}
                  sx={{ minWidth: 200, height: 56 }}
                >
                  Create new Insurance Client
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Valid Until"
                  name="validUntil"
                  type="date"
                  value={formData.validUntil}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Quotation expiry date"
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Internal notes..."
              />
            </Stack>
          </Paper>

          {/* Line Items */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Line Items</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addLineItem}
                size="small"
              >
                Add Item
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Product / Description</TableCell>
                    <TableCell width="10%">Qty</TableCell>
                    <TableCell width="15%">Unit Price</TableCell>
                    <TableCell width="12%">Discount</TableCell>
                    <TableCell width="12%">Tax %</TableCell>
                    <TableCell width="15%">Total</TableCell>
                    <TableCell width="6%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No items added. Click "Add Item" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.tempId}>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            value={item.product ?? (item.description || '')}
                            onChange={(e, newValue) => {
                              if (newValue && typeof newValue === 'object') {
                                updateLineItemFields(item.tempId, {
                                  product: newValue,
                                  description: newValue.attributes?.name || '',
                                  unitPrice: Number(newValue.attributes?.price) || item.unitPrice,
                                });
                              } else {
                                updateLineItemFields(item.tempId, {
                                  product: null,
                                  description: typeof newValue === 'string' ? newValue : '',
                                });
                              }
                            }}
                            onInputChange={(e, value) => {
                              if (value !== undefined && !item.product && (item.description || '') !== value) {
                                const isOption = products.some(
                                  (p) => (p.attributes?.name || '').toLowerCase() === (value || '').toLowerCase()
                                );
                                if (!isOption) {
                                  updateLineItemFields(item.tempId, { product: null, description: value || '' });
                                }
                              }
                            }}
                            options={products}
                            loading={productsLoading}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : `${option.attributes?.name || ''} (${option.attributes?.sku || ''})`.trim() || '—'
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select product or type description"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'quantity', e.target.value)
                            }
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                            allowDecimal={false}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'unitPrice', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.discount}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'discount', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">OMR</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.taxPercentage}
                            onChange={(e) =>
                              updateLineItem(item.tempId, 'taxPercentage', e.target.value)
                            }
                            inputProps={{ min: 0, max: 100, step: 0.5 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">%</InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            OMR {item.total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLineItem(item.tempId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Totals Summary */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="h6">OMR {formData.subtotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color="error">
                    Discount:
                  </Typography>
                  <Typography variant="h6" color="error">
                    - OMR {formData.discount.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Tax:</Typography>
                  <Typography variant="h6">OMR {formData.tax.toFixed(2)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5">Total:</Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    OMR {formData.total.toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/quotations')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Quotation' : 'Create Quotation'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default QuotationForm;

